import { UnifiedLiquidityEnum, useSecurity, type MarketDataCardConfig } from '@talos/kyoko';
import { useEffect, useMemo, useRef } from 'react';
import { useMarketDataMarketSelectorItems } from '../MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import { getMarketDataCardConfigAction } from './getMarketDataCardConfigAction';

interface UseInitializedMarketAccountsConfigParams {
  config: MarketDataCardConfig;
  onSave: (config: MarketDataCardConfig) => void;
  onDelete: (config: MarketDataCardConfig) => void;
}

export function useInitializedMarketAccountsConfig({
  config,
  onSave,
  onDelete,
}: UseInitializedMarketAccountsConfigParams): MarketDataCardConfig {
  const security = useSecurity(config.symbol);
  const lastActionKeyRef = useRef<string>();
  const items = useMarketDataMarketSelectorItems({
    symbol: config.symbol,
    includeUnifiedLiquidityMarkets: config.unifiedLiquidity === UnifiedLiquidityEnum.Enabled,
    queryMSSRecords: false,
  });

  const configAction = useMemo(
    () =>
      getMarketDataCardConfigAction({
        config,
        items,
        hasSecurity: security != null,
      }),
    [config, items, security]
  );

  useEffect(() => {
    if (!configAction) {
      return;
    }

    const actionKey =
      configAction.type === 'remove'
        ? `remove:${config.id}:${config.symbol ?? ''}`
        : `save:${JSON.stringify(configAction.config)}`;
    if (lastActionKeyRef.current === actionKey) {
      return;
    }
    lastActionKeyRef.current = actionKey;

    if (configAction.type === 'remove') {
      onDelete(config);
      return;
    }

    onSave(configAction.config);
  }, [config, configAction, onDelete, onSave]);

  return configAction?.type === 'save' ? configAction.config : config;
}
